import { useCallback } from "react";
import { BAR_RADIUS, ChartType, LegendKey } from "../../../../models/chart_model";
import { monthNames } from "../../../../pages/Dashboard/PriceForcating/IexMonthly";
import { COLORS, LARGE_DATA_THRESHOLD } from "../constants";
import { getLegendColor, getLegendColorKey } from "../utils/colorUtils";
import { EChartsSeries } from "../types";

export function useBuildSeries() {
  return useCallback(
    (
      chartData: any[],
      legendsToShow: LegendKey[],
      selected: LegendKey[],
      hidden: string[],
      colors: Record<string, string>,
      barGap: number,
      forecast: boolean,
      colorByPoint?: boolean,
      barColors?: string[],
    ): EChartsSeries[] => {
      const isLarge = chartData.length > LARGE_DATA_THRESHOLD;
      return legendsToShow
        .filter((legend) => !hidden.includes(legend.name || ""))
        .map((legend, index) => {
          const legendColorKey = getLegendColorKey(legend, index);
          const isSelected =
            selected.some((item) => item.name === legend.name) ||
            selected.length === 0;
          const color = !isSelected
            ? getLegendColor(legend, index, selected)
            : (legend.legendColor ??
              legend.stroke ??
              (colors[legendColorKey] ||
                getLegendColor(legend, index, selected)));
          const dashFlagKey: string | undefined = legend.dashFlagKey;
          let hasFlaggedPoint = false;
          const seriesData = chartData.map((item) => {
            const v = item[legend.dataKey];
            // ECharts crashes in getDataParams/getRawIndex when data values are
            // null — use undefined so ECharts treats them as gaps instead.
            if (v === null || v === undefined) return undefined;
            if (dashFlagKey && item[dashFlagKey]) {
              hasFlaggedPoint = true;
              const svg =
                "image://data:image/svg+xml;utf8," +
                encodeURIComponent(
                  '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="3.5" fill="#ef4444" stroke="#ffffff" stroke-width="1"/></svg>',
                );
              return {
                value: v,
                symbol: svg,
                symbolSize: 8,
              };
            }
            if (dashFlagKey) {
              return { value: v, symbol: "none" };
            }
            return v;
          });
          const commonOptions = {
            id: legend.dataKey,
            name: legend.name,
            data: seriesData,
            yAxisIndex: legend.yAxisId === "right" ? 1 : 0,
            itemStyle: { color },
            // Emit mouseover/mouseout when the cursor is on the line body, not
            // just on (hidden) symbols — required for active-series detection.
            triggerLineEvent: true,
            emphasis: { focus: "series" as const },
            blur: {
              itemStyle: { opacity: 0.25 },
              lineStyle: { opacity: 0.25 },
              areaStyle: { opacity: 0.1 },
            },
            // Disable animation for large datasets to avoid UI freeze
            animation: !isLarge,
            ...(isLarge
              ? {
                  large: true,
                  largeThreshold: 2000,
                  progressive: 3000,
                  progressiveThreshold: 5000,
                }
              : {}),
          };

          if (legend.type === ChartType.Bar) {
            const customBarColor = "#f1935c";
            const barSeriesData = chartData.map((item, idx) => {
              const rawVal = item[legend.dataKey];
              // Same null-guard as seriesData above — null/undefined causes
              // ECharts to crash in getDataParams/getRawIndex on hover.
              if (rawVal === null || rawVal === undefined) return undefined;
              const isModelled = !!(dashFlagKey && item[dashFlagKey]);
              const modelledStyle = isModelled
                ? {
                    borderColor: "#ef4444",
                    borderWidth: 2,
                    borderType: "solid" as const,
                  }
                : {};
              const legendYear = (legend.dataKey || "").split("_")[0];
              if (forecast) {
                const now = new Date();
                const currentMonth = monthNames[now.getMonth()];
                const currentYear = now.getFullYear().toString();
                if (
                  item.monthGroup === currentMonth &&
                  legendYear === currentYear
                ) {
                  return {
                    value: rawVal,
                    itemStyle: { color: customBarColor, ...modelledStyle },
                  };
                }
              }
              if (colorByPoint) {
                const palette =
                  barColors && barColors.length > 0 ? barColors : COLORS;
                return {
                  value: rawVal,
                  itemStyle: {
                    color: palette[idx % palette.length],
                    ...modelledStyle,
                  },
                };
              }
              if (isModelled) {
                return { value: rawVal, itemStyle: modelledStyle };
              }
              return { value: rawVal };
            });
            return {
              ...commonOptions,
              type: "bar",
              stack: legend.stackId || null,
              barGap: "0%",
              // Wire prop — fall back to "20%" when default 2.5 sentinel
              barCategoryGap:
                barGap !== undefined && barGap !== 2.5 ? `${barGap}%` : "20%",
              data: barSeriesData,
              // large mode skips individual bars on dense datasets — must be off
              // progressive is safe for bars and keeps rendering performant
              large: false,
              largeThreshold: undefined,
              itemStyle: {
                ...commonOptions.itemStyle,
                borderRadius: BAR_RADIUS,
              },
              // Bars must not enter blur state when tooltip hover-sync
              // emphasizes a sibling (line) series — keeps bars at full color.
              emphasis: { focus: "none", disabled: true },
              blur: {
                itemStyle: { opacity: 1 },
              },
            };
          } else if (legend.type === ChartType.Step) {
            return {
              ...commonOptions,
              type: "line",
              step: legend.step || "start",
              stack: legend.stackId || null,
              symbol: "none",
              showSymbol: hasFlaggedPoint,
              lineStyle: {
                width: 3,
                type: legend.isDash ? "dashed" : "solid",
              },
            };
          } else if (legend.type === ChartType.Area) {
            const isSinglePoint = seriesData.length === 1;
            return {
              ...commonOptions,
              type: "line",
              stack: legend.stackId || null,
              areaStyle: { color },
              showSymbol: isSinglePoint || hasFlaggedPoint,
              symbol: isSinglePoint ? "circle" : "none",
              symbolSize: isSinglePoint ? 6 : undefined,
              lineStyle: { width: 2, color },
            };
          } else {
            const isSinglePoint = seriesData.length === 1;
            return {
              ...commonOptions,
              // large + progressive only affect canvas rendering, NOT tooltip data
              // (sampling was the culprit for skipped tooltips — it's removed)
              ...(isLarge && !isSinglePoint
                ? {
                    large: true,
                    largeThreshold: 2000,
                    progressive: 3000,
                    progressiveThreshold: 5000,
                  }
                : {}),
              showSymbol: legend.showSymbol ?? (isSinglePoint || hasFlaggedPoint),
              symbolSize:
                legend.showSymbol ?? isSinglePoint
                  ? (legend.symbolSize ?? (isSinglePoint ? 6 : 4))
                  : undefined,
              hoverAnimation: false,
              animation: false,
              type: "line",
              smooth: legend.smooth ?? !isSinglePoint,
              symbol:
                legend.showSymbol ?? isSinglePoint
                  ? (legend.symbol ?? "circle")
                  : "none",
              lineStyle: { width: legend.lineWidth ?? 2, type: legend.isDash ? "dashed" : "solid", opacity: legend.opacity ?? 1 },
            };
          }
        });
    },
    [],
  );
}
